import { z } from 'zod'

import { looksLikePhone, normalisePhone, PHONE_MESSAGE } from './phone'

const nameHalf = (message: string) =>
  z
    .string({ message })
    .max(39, 'That name is too long.')
    .transform((v) => v.trim())
    .refine((v) => v.length > 0, message)

/**
 * The private half of the review gate: what someone tells Paul instead of
 * leaving a public review.
 *
 * It asks who they are and one way to reach them. A complaint nobody can
 * answer is one counted rather than fixed, and this is the form the unhappy
 * ones land on. One channel, not both: the point is to hear back from them,
 * so it asks for the least that makes a reply possible.
 */
export const feedbackSchema = z
  .object({
    firstName: nameHalf('Your first name, so we know who we are talking to.'),
    lastName: nameHalf('And your last name.'),
    email: z
      .string()
      .max(200, 'Email is too long.')
      .optional()
      .transform((v) => v?.trim() ?? '')
      .refine((v) => v === '' || z.string().email().safeParse(v).success, {
        message: 'Please enter a valid email address.',
      }),
    phone: z
      .string()
      .max(30, 'Phone number is too long.')
      .optional()
      .transform((v) => (v ? normalisePhone(v) : ''))
      .refine((v) => v === '' || looksLikePhone(v), { message: PHONE_MESSAGE }),
    rating: z.coerce
      .number()
      .int('Rating must be a whole number.')
      .min(1, 'Please select a star rating.')
      .max(5, 'Rating cannot exceed 5.'),
    message: z
      .string()
      .min(1, 'Please let us know what happened.')
      .max(2000, 'Message is too long.')
      .transform((v) => v.trim()),
  })
  .superRefine((data, ctx) => {
    if (!data.email && !data.phone) {
      ctx.addIssue({
        code: 'custom',
        path: ['email'],
        message: 'An email or a phone number, so we can get back to you.',
      })
    }
  })

/**
 * The name as the record keeps it, both halves joined. Both are optional on
 * the parsed type because every transformed field infers that way here; the
 * schema is what guarantees they arrive.
 */
export function feedbackName(data: { firstName?: string; lastName?: string }): string {
  return [data.firstName, data.lastName].filter(Boolean).join(' ')
}

export type FeedbackFormData = z.input<typeof feedbackSchema>
export type FeedbackParsed = z.output<typeof feedbackSchema>
